import {
  VaultError,
  MAX_REQUEST_BYTES,
  type ErrorCode,
  type SessionInfo,
} from "./model";
export const API_TIMEOUT_MS = 60000;
export class ApiClient {
  session: SessionInfo = { user: null, csrf: null, connection: null };
  async request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
    const payload = body === undefined ? undefined : JSON.stringify(body);
    if (payload && new TextEncoder().encode(payload).length > MAX_REQUEST_BYTES)
      throw new VaultError(
        "vault_too_large",
        "Checkpoint exceeds the supported request size.",
      );
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
    try {
      const response = await fetch(`/api${path}`, {
        method,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...(this.session.csrf ? { "X-CSRF-Token": this.session.csrf } : {}),
        },
        body: payload,
      });
      const data = (await response.json()) as T & {
        error?: { code: ErrorCode; message: string; recoveryToken?: string };
      };
      if (response.status === 401)
        this.session = { user: null, csrf: null, connection: null };
      if (!response.ok)
        throw new VaultError(
          data.error?.code ?? "offline",
          data.error?.message ?? "Repository request failed.",
          response.status,
          data.error?.recoveryToken,
        );
      return data;
    } catch (error) {
      if (error instanceof VaultError) throw error;
      throw new VaultError(
        "offline",
        controller.signal.aborted
          ? "GitHub timed out."
          : "Network unavailable.",
      );
    } finally {
      clearTimeout(timeout);
    }
  }
  /**
   * POSTs `body` and hands each line of the newline-delimited JSON reply to `onEvent` as it
   * arrives. There is no timeout: `signal` stops it. A connection lost part way is "offline".
   */
  async stream<T>(path: string, body: unknown, onEvent: (event: T) => void, signal?: AbortSignal): Promise<void> {
    let response: Response;
    try {
      response = await fetch(`/api${path}`, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          ...(this.session.csrf ? { "X-CSRF-Token": this.session.csrf } : {}),
        },
        body: JSON.stringify(body),
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new VaultError("offline", "Network unavailable.");
    }
    if (!response.ok) {
      if (response.status === 401)
        this.session = { user: null, csrf: null, connection: null };
      const data = (await response.json().catch(() => ({}))) as {
        error?: { code: ErrorCode; message: string };
      };
      throw new VaultError(
        data.error?.code ?? "offline",
        data.error?.message ?? "The request failed.",
        response.status,
      );
    }
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffered = "";
    while (true) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (error) {
        if (signal?.aborted) throw error;
        throw new VaultError("offline", "The connection dropped before the answer finished.");
      }
      buffered += decoder.decode(chunk.value, { stream: !chunk.done });
      const lines = buffered.split("\n");
      buffered = chunk.done ? "" : lines.pop()!;
      for (const line of lines) if (line.trim()) onEvent(JSON.parse(line) as T);
      if (chunk.done) return;
    }
  }
  async loadSession(): Promise<SessionInfo> {
    this.session = await this.request<SessionInfo>("/session");
    return this.session;
  }
}
